import arrayTools from "_src/engine/utility/arrays";
import {centroid, planeODR, getBboxSize, getBboxCenter} from "_src/engine/math";
import {geometry} from "_src/engine/constants";

import selector from "_src/manipulators/libs/selector";
import widgets from "_src/client/widgets/selection_shapes";
import pixels from "_src/client/pixels";
import appState from "_src/app_state";
import workspace from "_src/workspace";
import windowManager from "_src/client/window_manager";
import AbstractManipulator from "_src/client/manipulators/abstract_manipulator";
import renderGraph from "_src/render_graph/render_graph";
import {screenUtils} from "_src/client/utils";
import history from "_src/history/manager";
import {stepType} from "_src/constants";
import events from "_src/engine/events";
import {vec3, quat, mat4} from "_src/libs/gl-matrix";
import output from "_src/engine/output";
import config from "_src/config";
import zstdCoder from "_src/engine/loaders/zstd/zstd_coder";
import gizmo from "_src/client/controls/gizmo";
import {BufferReaderType} from "_src/client/buffer_reader"

const _VEC3_TMP = vec3.create();
const _VEC3_TMP2 = vec3.create();

const SELECTION = 0;
const MOVING    = 1;

export default class Selector extends AbstractManipulator {

  constructor(camera) {

    super(camera);

    this._ndcX = 0;
    this._ndcY = 0;
    this._cutOffStep = SELECTION;
    this._isMoving = false;
    this._storedSteps = [];
    this._newProcessedObjects = new Set();
    this._selectionPromise = Promise.resolve(null);

  }

  setSelectionType(type) {
    const oldType = appState.getStateValue("selectionType");

    widgets.setSelectionTypeEnabled(oldType, false);
    widgets.setSelectionTypeEnabled(type, true);

    if (type === appState.ST_CUT_OFF_PLANE) {
      this._cutOffStep = SELECTION;
    }
    if (oldType === appState.ST_CUT_OFF_PLANE) {
      this._selectByPlane(null, null, false);
    }

    setSelectionPlaneEnabled(false);
    if (type === appState.ST_CUT_OFF_PLANE || oldType === appState.ST_CUT_OFF_PLANE) {
      history.removeSelectionSteps();
    }

  }

  //Reimplement this method in child class to change selector behaviour
  _checkCanSelect(object) {
    return workspace.checkIsReadyToSelect(object);
  }

  _onStart() {

    const selectionType = appState.getStateValue("selectionType");

    this._storedSteps.length = 0;

    this.setSelectionType(selectionType);

  }

  _onStop() {
    this.setSelectionType(appState.ST_NONE);
  }

  _startMoveHandler(screenX, screenY) {

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), this._camera.viewport, screenX, screenY);

    const type = appState.getStateValue("selectionType");
    const flatSelectionType = type === appState.ST_CIRCLE || type === appState.ST_CUT_OFF_PLANE ||
      type === appState.ST_RECTANGLE || type === appState.ST_LASSO;

    if (flatSelectionType) {
      appState.setStateValue("manipulatorIsActive", true);
    }

    this._selectionStart(x, y);
    this._moveSelectionWidget(x, y);
    this._select(screenX, screenY, type);

    mainWindow.renderGraph.requestFrame(!flatSelectionType);
  }

  _mouseMoveHandler({screenX, screenY, active}) {

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), this._camera.viewport, screenX, screenY);

    const selectionType = appState.getStateValue("selectionType");

    switch (selectionType) {
      case appState.ST_CIRCLE:
        widgets.setCircleLocation(x, y);
        break;
      case appState.ST_CUT_OFF_PLANE:
        if (this._cutOffStep == SELECTION) {
          widgets.setCircleLocation(x, y);
        } else {
          this._moveGizmo(screenX, screenY, active);
        }
        break;
      case appState.ST_SPHERE:
        this._moveSphereToCursor(screenX, screenY);
        break;
    }

    if (active) {
      if (!this._isMoving) {
        this._isMoving = true;
      }

      this._moveSelectionWidget(x, y);
      this._select(screenX, screenY, selectionType);
    } else {
      if (this._isMoving) {
        this._isMoving = false;
        this._stopMoveHandler();
      }
    }
  }

  _stopMoveHandler() {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);

    const type = appState.getStateValue("selectionType");
    const mode = appState.getStateValue("selectionMode");

    if (type == appState.ST_CIRCLE || type == appState.ST_CUT_OFF_PLANE ||
        type == appState.ST_RECTANGLE || type == appState.ST_LASSO) {
      appState.setStateValue("manipulatorIsActive", false);
    }

    const selectPayload = {
      type,
      mode,
    };

    switch (type) {
      case appState.ST_CIRCLE:
      case appState.ST_RECTANGLE:
      case appState.ST_LASSO: {
        widgets.setContourAnimated(false);
        return this._selectionPromise.then(() => {
          const oneSideSelection = !appState.getStateValue("selectThrough");
          this._selectionPromise = this._doMaskSelection(this._camera, oneSideSelection).then(({maskPixels, numSelected}) => {

            if (numSelected === 0) {
              if (config.enableManipulatorsWarnings) {
                output.warn("Nothing selected after 2d selection [1]:\n" + workspace.getVerbosityInfo());
              }
              mainWindow.renderGraph.requestFrame();
              return;
            }

            const oneSideSelection = !appState.getStateValue("selectThrough");
            const mainWindowViewport = windowManager.getWindow(windowManager.MAIN).getViewport();

            const objects = workspace.getObjects().filter((object) => {
              return this._checkCanSelect(object);
            });

            const selectedObjects = getSelectedObjects(objects);
            pushSelectedChunksToHistory(objects);
            selectedObjects.forEach(object => this._newProcessedObjects.add(object));

            for (let object of objects) {
              this.updateSelection(object);
            }

            if (this._newProcessedObjects.size > 0) {

              history.addStep(stepType.SELECT, selectPayload);

              return zstdCoder.getCoder().then((simple) => {

                const bitArrayMask = arrayTools.arrayToBitArray(maskPixels);
                const compressedMask = simple.compress(bitArrayMask);
                const camera = this._camera;

                selectPayload.compressedMask = compressedMask;
                selectPayload.cameraType = camera.type;
                selectPayload.screenWidth = mainWindowViewport[2];
                selectPayload.screenHeight = mainWindowViewport[3];
                selectPayload.cameraTrans = camera.getTranslation();
                selectPayload.cameraQuat = camera.getRotation();
                selectPayload.cameraNear = camera.near;
                selectPayload.cameraFar = camera.far;
                selectPayload.cameraTop = camera.top;
                selectPayload.cameraBottom = camera.bottom;
                selectPayload.steps = null;
                selectPayload.oneSide = oneSideSelection;
                selectPayload.objects = packSelectedObjectsToPayload(this._newProcessedObjects);

                events.emit("select");

                this._newProcessedObjects.clear();

                mainWindow.renderGraph.requestFrame();

              });

            } else if (config.enableManipulatorsWarnings) {
              output.warn("Nothing selected after 2d selection [2]:\n" + workspace.getVerbosityInfo());
            }

          });
          return this._selectionPromise;
        });
      }
      case appState.ST_CUT_OFF_PLANE: {
        if (this._cutOffStep == SELECTION) {
          this._selectionPromise = this._poseCutOffPlane();
        } else {
          this._stopMovingGizmo();
        }
        return this._selectionPromise;
      }
      case appState.ST_SPHERE: {
        if (this._newProcessedObjects.size > 0) {

          selectPayload.steps = this._storedSteps.slice();
          selectPayload.objects = packSelectedObjectsToPayload(this._newProcessedObjects);

          history.addStep(stepType.SELECT, selectPayload);

          events.emit("select");

          this._newProcessedObjects.clear();
        } else if (config.enableManipulatorsWarnings) {
          if (this._storedSteps.length > 0) {
            output.warn("Nothing selected after 3d selection:\n" + workspace.getVerbosityInfo());
          } else {
            output.warn("Nothing selected after 3d selection: no selection steps");
          }
        }
        this._storedSteps.length = 0;
        mainWindow.renderGraph.requestFrame();
      }
    }

  }

  _select(clientX, clientY, type) {
    if (type == appState.ST_SPHERE) {
      this._realtimeSphereSelection(clientX, clientY);
    }
  }

  _mouseDownHandler({screenX, screenY}) {
    this._startMoveHandler(screenX, screenY);
    if (this._cutOffStep == MOVING) {
      gizmo.pick(this._camera, screenX, screenY);
    }
  }

  _mouseUpHandler() {
    this._stopMoveHandler();
    this._isMoving = false;
  }

  _realtimeSphereSelection(clientX, clientY) {

    this._getSpherePosition(clientX, clientY).then(center => {
      const sphereRadius = widgets.getSphereRadius();

      const objects = workspace.getObjects();

      for (let object of objects) {

        if (!this._checkCanSelect(object)) {
          continue;
        }

        selector.selectSphere(object, center, sphereRadius);

      }

      const selectedObjects = getSelectedObjects(objects);
      pushSelectedChunksToHistory(objects);
      selectedObjects.forEach(object => this._newProcessedObjects.add(object));

      for (let object of objects) {

        if (!this._checkCanSelect(object)) {
          continue;
        }

        this.updateSelection(object);

      }

      this._storedSteps.push( {position: vec3.clone(center), radius: sphereRadius} );

    });
  }

  _moveSphereToCursor(screenX, screenY) {

    this._getSpherePosition(screenX, screenY).then(position => {
      widgets.setSphereLocation(position);
      widgets.updateSphereRadius();
      windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
    });
  }

  async _getSpherePosition(x, y) {

    const camera = this._camera;
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const spherePosition = _VEC3_TMP;
    const object = await mainWindow.getPixelWorldPoint(x, y, camera, spherePosition);

    if (!object) {
      return this.recalcPointXY(x, y, spherePosition, _VEC3_TMP2);
    }

    return spherePosition;
  }

  recalcPointXY(textureX, textureY, point, dest) {
    const camera = this._camera;
    const mainWindow = windowManager.getWindow(windowManager.MAIN);

    const projectedPoint = camera.projectPoint(point[0], point[1], point[2], dest);
    const depth = projectedPoint[2];
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, textureX, textureY);

    return camera.unprojectPoint(x, y, depth, dest);
  }

  _selectionStart(ndcX, ndcY) {

    const type = appState.getStateValue("selectionType");

    switch (type) {
      case appState.ST_CIRCLE:
      case appState.ST_CUT_OFF_PLANE:
        widgets.setMaskVisibility(true);
        widgets.setMaskSceneCleared(false);
        widgets.startCircleLocation(ndcX, ndcY);
        break;
      case appState.ST_LASSO:
      case appState.ST_RECTANGLE:
        widgets.setContourAnimated(true);
        break;
    }

    this._ndcX = ndcX;
    this._ndcY = ndcY;

  }

  _setSelectionRectangle(ndcX, ndcY) {

    let minx = Math.min(ndcX, this._ndcX);
    let maxx = Math.max(ndcX, this._ndcX);
    let miny = Math.min(ndcY, this._ndcY);
    let maxy = Math.max(ndcY, this._ndcY);

    widgets.setRectangleCorner(minx, miny, maxx, maxy);

  }

  _moveSelectionWidget(ndcX, ndcY) {

    const type = appState.getStateValue("selectionType");

    switch (type) {
      case appState.ST_CIRCLE:
      case appState.ST_CUT_OFF_PLANE:
        widgets.setCircleLocation(ndcX, ndcY);
        break;
      case appState.ST_RECTANGLE:
        this._setSelectionRectangle(ndcX, ndcY);
        widgets.setContourAnimationStopped(true);
        break;
      case appState.ST_LASSO:
        widgets.addLassoPoint(ndcX, ndcY);
        widgets.setContourAnimationStopped(true);
        break;
    }

    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame(false);
  }

  findObjectIntersection(ndcX, ndcY, object, camera, dest) {

    if (!dest) {
      dest = vec3.create();
    }

    const drawables = object.getAllLoadedDrawables();
    const intersectedDrawable = selector.findClosestIntersection(dest, ndcX, ndcY, camera, drawables);

    return intersectedDrawable ? dest : null;

  }

  _findClosestIntersection(ndcX, ndcY, camera, dest) {

    const objects = workspace.getObjects().filter((object) => {
      return this._checkCanSelect(object);
    });
    let drawables = [];
    for (const object of objects) {
      drawables = drawables.concat(object.getAllLoadedDrawables());
    }
    const intersectedDrawable = selector.findClosestIntersection(dest, ndcX, ndcY, camera, drawables);

    return Boolean(intersectedDrawable);

  }

  _doMaskSelection(camera, oneSideSelection) {

    const type = appState.getStateValue("selectionType");

    return getColorAndDepth(type, oneSideSelection).then(({ maskPixels, depthPixels }) => {

      const objects = workspace.getObjects().filter((object) => {
        return this._checkCanSelect(object);
      });

      let numSelected = 0;

      if (selector.checkSelectionMask(maskPixels)) {

        const selectionBoundings = selector.getMaxBoundings(maskPixels, camera.viewport[2], camera.viewport[3]);

        for (let object of objects) {
          numSelected += selector.selectByMask(object, camera, maskPixels, depthPixels, selectionBoundings, oneSideSelection);
        }

      }

      if (oneSideSelection) {
        arrayTools.returnArrayBuffer(depthPixels);
      }

      switch (type) {
        case appState.ST_CIRCLE:
        case appState.ST_CUT_OFF_PLANE:
          widgets.resetCircle();
          widgets.setMaskVisibility(false);
          break;
        case appState.ST_RECTANGLE:
          widgets.resetRectangle();
          break;
        case appState.ST_LASSO:
          widgets.resetLasso();
          break;
      }

      return { maskPixels, numSelected };

    });

  }

  _checkSelection() {
    const objects = workspace.getObjects().filter((object) => {return this._checkCanSelect(object)});

    for (let object of objects) {
      for (let frame of object.frames) {
        for (let chunk of frame.model.chunks) {

          const geometry = chunk.geometry;

          const selectionMask = geometry.getItemBuffer("a_selection_mask");
          if (selectionMask == null) {
            output.error(output.DEVELOPERS_ERROR, "Can't check selected objects. Invalid geometry. Missing selection mask.");
            continue;
          }

          const hasSelectedVertices = geometry.checkHasSelectedVertices();

          if (hasSelectedVertices) {
            return true;
          }
        }
      }
    }

    return false;
  }

  updateSelection(object) {

    const isSelection = appState.getStateValue("selectionMode") === appState.SM_SELECT;

    for (let frame of object.frames) {

      const model = frame.model;

      const chunks = model.chunks;

      for (let chunk of chunks) {

        const geometry = chunk.geometry;

        if (!geometry.checkHasMarkedVertices()) {
          continue;
        }

        selector.applySelection(chunk, isSelection);

      }

    }

  }

  _poseCutOffPlane() {

    return this._doMaskSelection(this._camera, true).then(() => {

      const mainWindow = windowManager.getWindow(windowManager.MAIN);
      mainWindow.renderGraph.requestFrame();

      const objects = workspace.getObjects().filter((object) => {
        return this._checkCanSelect(object);
      });

      const selectedPoints = [];

      for (let object of objects) {
        findSelectedPoints(object, selectedPoints);
      }

      if (selectedPoints.length === 0) {
        return;
      }

      const point = centroid(selectedPoints, vec3.create());
      const normal = planeODR(selectedPoints, point, vec3.create());

      const view = vec3.fromValues(0, 0, -1);
      vec3.transformQuat(view, view, this._camera.quat);

      if (vec3.dot(normal, view) < 0.0) {
        vec3.scale(normal, normal, -1);
      }

      const minmax = workspace.getBBox((object) => {return object.loaded && object.getVisibility()});
      const size = getBboxSize(minmax);
      const center = getBboxCenter(minmax, vec3.create());
      const rotation = quat.rotationTo(quat.create(), geometry.AXIS_Z, normal);

      const dist = vec3.dot(center, normal) - vec3.dot(point, normal);
      vec3.scaleAndAdd(point, center, normal, -dist);

      widgets.setPlaneVisibility(true);
      widgets.setPlaneLocation(point);
      widgets.setPlaneRotation(rotation);
      widgets.setPlaneSize(size);

      history.writeSelectionPlaneToCurrentStep(this);
      this._selectByPlane(point, normal);

      this._cutOffStep = MOVING;

      setSelectionPlaneEnabled(true);
      setSelectionPlaneTransform(point, rotation);
      gizmo.updateGizmo(this._camera);
      widgets._setCircleModeEnabled(false);

    });

  }

  _selectByPlane(center, normal, writeHistory = true) {

    const objects = workspace.getObjects().filter((object) => {
      return this._checkCanSelect(object);
    });

    for (let object of objects) {
      selector.dropSelection(object);
      if (center && normal) {
        selector.selectByPlane(object, normal, center);
      }
    }

    const selectedObjects = getSelectedObjects(objects);
    selectedObjects.forEach(object => this._newProcessedObjects.add(object));

    if (writeHistory) {
      const selectPayload = {
        type    : appState.ST_CUT_OFF_PLANE,
        mode    : appState.getStateValue("selectionMode"),
        point   : vec3.clone(center),
        normal  : vec3.clone(normal),
        objects : packSelectedObjectsToPayload(this._newProcessedObjects)
      };

      history.addStep(stepType.SELECT, selectPayload);
      history.writeSelectionPlaneToCurrentStep(this, center, normal);
    }
    this._newProcessedObjects.clear();

    for (let object of objects) {
      this.updateSelection(object);
    }

  }

  _cameraMoveHandler() {
    if (this._cutOffStep == MOVING) {
      gizmo.updateGizmo(this._camera);
    }
  }

  _moveGizmo(screenX, screenY, active) {

    if (active) {

      if (!gizmo.isActive()) {
        return;
      }

      if (this._isMoving) {
        gizmo.transform(this._camera, screenX, screenY);
      } else {
        gizmo.startMove(this._camera, screenX, screenY);
      }
    } else {
      gizmo.resetHighlight();
      gizmo.highlight(this._camera, screenX, screenY);
    }

    windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame(active && this._isMoving);

  }

  _stopMovingGizmo() {

    if (!gizmo.isActive()) {
      return;
    }

    gizmo.stopMove();
    gizmo.updateGizmo(this._camera);

    const center = gizmo.getPosition();
    const normal = vec3.clone(geometry.AXIS_Z);
    vec3.transformQuat(normal, normal, gizmo.getRotation());

    this._selectByPlane(center, normal)

    this._selectionPromise = Promise.resolve(null);

  }

  applyHistoryToPlane(point, normal) {
    if (point && normal) {
      if (this._cutOffStep !== MOVING) {
        widgets.setPlaneVisibility(true);
        this._cutOffStep = MOVING;
        setSelectionPlaneEnabled(true);
        widgets._setCircleModeEnabled(false);
      }

      const rotation = quat.rotationTo(quat.create(), geometry.AXIS_Z, normal);
      gizmo.setRotation(rotation);
      widgets.plane.copyRotation(rotation);

      gizmo.setPosition(point);
      widgets.plane.copyTranslation(point);

      gizmo.updateGizmo(this._camera);

    } else {
      this.clear();
    }

    this._selectByPlane(point, normal, false);
  }

  invert() {
    const type = appState.getStateValue("selectionType");

    if (type == appState.ST_CUT_OFF_PLANE) {

      const rotation = quat.setAxisAngle(quat.create(), geometry.AXIS_X, Math.PI);

      const gizmoRot = gizmo.getRotation();
      quat.multiply(gizmoRot, gizmoRot, rotation);

      const planeRot = widgets.plane.getRotation();
      quat.multiply(planeRot, planeRot, rotation);

      gizmo.setRotation(gizmoRot);
      widgets.plane.copyRotation(planeRot);

      gizmo.updateGizmo(this._camera);

    }
  }

  clear() {
    const type = appState.getStateValue("selectionType");

    if (type == appState.ST_CUT_OFF_PLANE) {
      this._cutOffStep = SELECTION;
      setSelectionPlaneEnabled(false);
      widgets.setSelectionTypeEnabled(type, true);
    }
  }

}

function getSelectedObjects(objects) {

  const selectedObjects = [];

  for (let object of objects) {

    if (!workspace.checkIsReady(object)) {
      continue;
    }

    let selected = false;

    for (let frame of object.frames) {
      for (let chunk of frame.model.chunks) {
        if (chunk.geometry.checkHasMarkedVertices()) {
          selected = true;
          break;
        }
      }
      if (selected) {
        break;
      }
    }

    if (selected) {
      selectedObjects.push(object);
    }

  }

  return selectedObjects;

}

function pushSelectedChunksToHistory(objects) {

  for (let object of objects) {

    if (!workspace.checkIsReady(object)) {
      continue;
    }

    for (let frame of object.frames) {

      const selectedChunksIDs = [];

      for (let chunk of frame.model.chunks) {
        if (chunk.geometry.checkHasMarkedVertices()) {
          selectedChunksIDs.push(chunk.getID());
        }
      }

      if (selectedChunksIDs.length > 0) {
        history.writeFrameToCurrentStep({type: stepType.SELECT, frame: frame, chunksUp: selectedChunksIDs});
      }
    }

  }

}

async function getColorAndDepth(selectionType, oneSideSelection) {

  const mainWindow = windowManager.getWindow(windowManager.MAIN);

  const maskPixels = await pixels.requestSelectionMask(selectionType);

  let depthPixels = null;
  if (oneSideSelection) {
    await mainWindow.bufferReader.readPixelCache(0, 0, BufferReaderType.GENERAL_PURPOSE); //dummy call to be sure that depth buffer is updated
    depthPixels = mainWindow.bufferReader.getBufferData(BufferReaderType.GENERAL_PURPOSE);
  }

  return ({ maskPixels, depthPixels });
}

function packSelectedObjectsToPayload(selectedObjects) {
  const payload = [];
  for (const object of selectedObjects) {
    payload.push({
      id: object.id,
      localMatrix: mat4.clone(object.model.localMatrix),
    });
  }

  return payload;
}

function findSelectedPoints(object, positions) {

  for (let frame of object.frames) {

    const model = frame.model;

    const chunks = model.chunks;

    for (let chunk of chunks) {

      const geometry = chunk.geometry;

      if (!geometry.checkHasMarkedVertices()) {
        continue;
      }

      collectPositions(geometry, positions, model.modelMatrix);

    }

  }

}

const collectPositions = (function() {

  const vec3Tmp = vec3.create();

  return function (geometry, points, transform) {

      const activeSelectionMask = geometry.getItemBuffer("active_selection_mask");
      const positions = geometry.getItemBuffer("a_position");

      for (let i = 0; i < activeSelectionMask.length; ++i) {
        if (activeSelectionMask[i] === 1) {
          const offset = 3 * i;
          const point = vec3.set(vec3Tmp, positions[offset], positions[offset + 1], positions[offset + 2]);

          vec3.transformMat4(point, point, transform);
          points.push([point[0], point[1], point[2]]);
        }
      }

      geometry.setHasMarkedVertices(false);

  }

})();

function setSelectionPlaneEnabled(enabled) {
  const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
  mainRenderGraph.setEnabled(renderGraph.TRANSFORM, enabled);

  gizmo.clearAttachedObjects();

  if (enabled) {
    gizmo.attachObject(widgets.plane);
  }

}

function setSelectionPlaneTransform(trans, rot) {
  gizmo.setPosition(trans);
  gizmo.setRotation(rot);
}
